import { useEffect, useMemo, useSyncExternalStore } from 'react';
import {
  LinkBase,
  useGetResourceLabel,
  useResourceContext,
  useTranslate,
} from 'ra-core';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbPage,
} from '../../components/admin/breadcrumb';
export type TitleBreadcrumbLink = {
  label: string;
  href: string;
};

export type TitleBreadcrumb = string | TitleBreadcrumbLink;

const BREADCRUMB_CONTAINER_ID = 'breadcrumb';

const getIsBreadcrumbContainerReady = () =>
  !!document.getElementById(BREADCRUMB_CONTAINER_ID);

/**
 * The kit `Breadcrumb` portals into `#breadcrumb` but only looks for it while rendering, and the layout header
 * may not be in the DOM yet (first render, lazy/suspended layout). Watch the DOM until it appears.
 */
const subscribeToBreadcrumbContainer = (onChange: () => void) => {
  if (getIsBreadcrumbContainerReady()) return () => undefined;

  const observer = new MutationObserver(() => {
    if (getIsBreadcrumbContainerReady()) {
      observer.disconnect();
      onChange();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
};

const useIsBreadcrumbContainerReady = () =>
  useSyncExternalStore(
    subscribeToBreadcrumbContainer,
    getIsBreadcrumbContainerReady,
    () => false
  );

/**
 * Renders the current page's breadcrumb in the header (`#breadcrumb`) + sets the document title to match.
 * Without a breadcrumb, shows the plural resource label.
 * @example
 * ```tsx
 * <MoonstarTitle
 *   breadcrumb={[{ label: 'Users', href: '/users' }, 'John Doe', 'Analytics']}
 * />
 * ```
 */
export function MoonstarTitle({
  breadcrumb,
}: {
  breadcrumb?: TitleBreadcrumb[];
}) {
  const translate = useTranslate();
  const resource = useResourceContext();
  const getLabel = useGetResourceLabel();
  const isContainerReady = useIsBreadcrumbContainerReady();

  const entries: TitleBreadcrumb[] = breadcrumb ?? [getLabel(resource!, 2)];

  return (
    <>
      <MoonstarTitleMeta breadcrumb={breadcrumb} />
      {isContainerReady && (
        <Breadcrumb>
          {entries.map((entry, index) => {
            const key = `${typeof entry === 'string' ? entry : entry.label}-${index}`;

            if (typeof entry !== 'string') {
              return (
                <BreadcrumbItem key={key}>
                  <LinkBase to={entry.href}>{translate(entry.label)}</LinkBase>
                </BreadcrumbItem>
              );
            }

            return index === entries.length - 1 ? (
              <BreadcrumbPage key={key}>{translate(entry)}</BreadcrumbPage>
            ) : (
              <BreadcrumbItem key={key}>{translate(entry)}</BreadcrumbItem>
            );
          })}
        </Breadcrumb>
      )}
    </>
  );
}

export function MoonstarTitleMeta({
  breadcrumb,
}: {
  breadcrumb?: TitleBreadcrumb[];
}) {
  const translate = useTranslate();
  const resource = useResourceContext();
  const getLabel = useGetResourceLabel();

  const metaTitle = useMemo(() => {
    if (!breadcrumb) {
      return getLabel(resource!, 2);
    }

    return breadcrumb
      .toReversed()
      .map((entry) =>
        translate(typeof entry === 'string' ? entry : entry.label)
      )
      .join(' | ');
  }, [breadcrumb, getLabel, resource, translate]);

  // Set imperatively rather than with a React 19 `<title>`: with several `<title>` elements the browser only uses
  // the first one. Restore the app name on unmount so pages without a title don't keep the previous one.
  useEffect(() => {
    const appName = translate('moonstar.appName');
    document.title = `${metaTitle} | ${appName}`;
    return () => {
      document.title = appName;
    };
  }, [metaTitle, translate]);

  return null;
}

/** The text of the last breadcrumb entry, used as the page heading. */
export const useBreadcrumbPageTitle = (breadcrumb?: TitleBreadcrumb[]) => {
  const translate = useTranslate();
  const last = breadcrumb?.at(-1);

  if (last === undefined) {
    return undefined;
  }

  return translate(typeof last === 'string' ? last : last.label);
};
